/*
 * Collection view toggle — requirements p36, "View: 2 options as shown in ref".
 *
 * The collection section already publishes its column counts as custom
 * properties on #Collection--<id> (--COLUMNS, --COLUMNS-MEDIUM,
 * --COLUMNS-SMALL, --COLUMNS-MOBILE), so switching view is four inline
 * custom-property writes on one element: no refetch, no re-render, no layout
 * library. The `sizes` attribute is rewritten alongside them so the roomier
 * view does not serve images cut for the denser one. The shopper's choice is
 * remembered in localStorage.
 */
class CollectionView extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('[data-section-id]');
    this.buttons = Array.from(this.querySelectorAll('[data-view]'));
    if (!this.section || this.buttons.length < 2) return;

    this.grid = this.section.querySelector('[data-products-grid]');
    this.storageKey = 'tlg:collection-view';

    const stored = this.read();
    if (stored && stored !== this.dataset.default) this.apply(stored, false);

    this.addEventListener('click', this.onClick);
    this.observer = new MutationObserver(() => {
      const selected = this.buttons.find((item) => item.getAttribute('aria-pressed') === 'true');
      if (selected) this.apply(selected.dataset.view, false);
    });
    if (this.grid) this.observer.observe(this.grid, {childList: true, subtree: true});
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  onClick(event) {
    const button = event.target.closest('[data-view]');
    if (!button || button.getAttribute('aria-pressed') === 'true') return;
    event.preventDefault();
    this.apply(button.dataset.view, true);
  }

  read() {
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (error) {
      return null;
    }
  }

  apply(view, persist) {
    const button = this.buttons.find((item) => item.dataset.view === view);
    if (!button) return;

    const style = this.section.style;
    style.setProperty('--COLUMNS', button.dataset.columns);
    style.setProperty('--COLUMNS-MEDIUM', button.dataset.columnsMedium);
    style.setProperty('--COLUMNS-SMALL', button.dataset.columnsSmall);
    style.setProperty('--COLUMNS-MOBILE', button.dataset.columnsMobile);

    if (this.grid && button.dataset.sizes) {
      this.grid.querySelectorAll('img[sizes]').forEach((image) => {
        image.sizes = button.dataset.sizes;
      });
    }

    this.buttons.forEach((item) => {
      item.setAttribute('aria-pressed', String(item === button));
    });

    if (persist) {
      try {
        window.localStorage.setItem(this.storageKey, view);
      } catch (error) {
        /* private mode — the view still switches, it just is not remembered */
      }
    }
  }
}

if (!customElements.get('collection-view')) {
  customElements.define('collection-view', CollectionView);
}
